// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/maintenance/state.ts
// Parameters       : none — below this header, identical across the fleet
// Destination      : packages/fleet/maintenance/src/state.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo folio-office-app
//     node fleet/fleet.ts check --repo folio-office-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
/**
 * Client-side view of the server's maintenance window.
 *
 * The server publishes the window on `/api/system/health` (see
 * `apps/server/src/handlers/system.rs`). This module keeps the last observed
 * snapshot in ONE place so the pieces that must react to it can read it without
 * being wired to each other:
 *
 * - the PowerSync connector's `canUploadNow` gate (pause uploads);
 * - the auto-connect controller (disconnect PowerSync entirely);
 * - the connectivity machine / UI (tell the user, or stay silent).
 *
 * A module-level singleton is the right shape here, not a shortcut: a
 * maintenance window is a property of the SERVER, so every account, every DB
 * instance and every connector in this runtime shares exactly one.
 *
 * The 503 the server returns on a paused write is the safety net for clients
 * that never saw the window (an old build, a probe that failed). THIS is the
 * primary mechanism: stop hammering, and reconnect on our own when it lifts.
 */

/** What the window stops. Mirrors `core_rust::maintenance::MaintenanceScope`. */
export type MaintenanceScope = "writes" | "sync" | "all";

export interface MaintenanceSnapshot {
  active: boolean;
  scopes: MaintenanceScope[];
  /** `null` = SILENT window: the user is told nothing. */
  message: string | null;
  /** Advisory only. It never lifts the window on its own. */
  until: string | null;
}

export const MAINTENANCE_OFF: MaintenanceSnapshot = Object.freeze({
  active: false,
  scopes: [] as MaintenanceScope[],
  message: null,
  until: null,
});

const KNOWN_SCOPES = new Set<string>(["writes", "sync", "all"]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isScope(value: unknown): value is MaintenanceScope {
  return typeof value === "string" && KNOWN_SCOPES.has(value);
}

/**
 * Parses the `maintenance` object of a health response.
 *
 * Deliberately tolerant: a server that predates this feature sends no
 * `maintenance` key at all, and that must read as "no window" — never as a
 * crash in the health poller, which would latch the API status to `down`.
 *
 * `fallbackMessage` carries the TOP-LEVEL `message` field so a server that only
 * emits the legacy shape still reaches the user.
 */
export function parseMaintenance(raw: unknown, fallbackMessage?: string): MaintenanceSnapshot {
  if (!isRecord(raw)) {
    return MAINTENANCE_OFF;
  }
  const source = raw;
  if (source.active !== true) {
    return MAINTENANCE_OFF;
  }
  const scopes = Array.isArray(source.scopes) ? source.scopes.filter(isScope) : [];
  const message =
    typeof source.message === "string" && source.message.trim() !== ""
      ? source.message
      : (fallbackMessage ?? null);
  return {
    active: true,
    // An active window with no recognizable scope is treated as `all`: the same
    // rule the server applies. A scope we cannot read must protect MORE, never
    // less.
    scopes: scopes.length > 0 ? scopes : ["all"],
    message,
    until: typeof source.until === "string" ? source.until : null,
  };
}

/** Does this snapshot stop writes? Any scope does. */
export function pausesWrites(snapshot: MaintenanceSnapshot): boolean {
  return snapshot.active && snapshot.scopes.length > 0;
}

/** Does this snapshot stop PowerSync entirely (downloads included)? */
export function pausesSync(snapshot: MaintenanceSnapshot): boolean {
  return snapshot.active && snapshot.scopes.some((s) => s === "sync" || s === "all");
}

// ── The singleton ───────────────────────────────────────────────────────────

let current: MaintenanceSnapshot = MAINTENANCE_OFF;
const listeners = new Set<(snapshot: MaintenanceSnapshot) => void>();

function sameSnapshot(a: MaintenanceSnapshot, b: MaintenanceSnapshot): boolean {
  return (
    a.active === b.active &&
    a.message === b.message &&
    a.until === b.until &&
    a.scopes.length === b.scopes.length &&
    a.scopes.every((s, i) => s === b.scopes[i])
  );
}

export function readMaintenance(): MaintenanceSnapshot {
  return current;
}

/**
 * Publishes a new snapshot. Notifies subscribers ONLY when something actually
 * changed: the health poller calls this every 15s, and waking the auto-connect
 * effect on every tick would churn the PowerSync connection.
 */
export function setMaintenance(snapshot: MaintenanceSnapshot): void {
  if (sameSnapshot(current, snapshot)) {
    return;
  }
  current = snapshot;
  for (const listener of listeners) {
    listener(snapshot);
  }
}

export function subscribeMaintenance(
  listener: (snapshot: MaintenanceSnapshot) => void,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test-only reset. Keeps suites from leaking a window into each other. */
export function resetMaintenance(): void {
  current = MAINTENANCE_OFF;
  listeners.clear();
}

/** Convenience: are writes paused right now? Used by `canUploadNow`. */
export function isWritePaused(): boolean {
  return pausesWrites(current);
}

/** Convenience: is PowerSync supposed to stay disconnected right now? */
export function isSyncPaused(): boolean {
  return pausesSync(current);
}
